import React, { useEffect, useRef, useState } from 'react';
import { CreditCard, Check, X } from 'lucide-react';

/**
 * Capture d'un badge RFID.
 *
 * Le lecteur se comporte comme un clavier : il « tape » l'identifiant du badge
 * puis envoie Entrée. On pose donc un champ transparent au-dessus d'un
 * affichage, on intercepte les frappes, et on valide sur Entrée.
 *
 * Le minuteur est la pièce essentielle : un lecteur envoie ses caractères en
 * quelques millisecondes, un humain non. Sans cette remise à zéro, une frappe
 * manuelle au clavier finirait par composer un identifiant.
 *
 * `valeur` est l'identifiant actuellement retenu, `onChange` reçoit le nouveau
 * (ou une chaîne vide quand on détache).
 */
const DELAI_REMISE_A_ZERO_MS = 500;

const ScanBadgeRfid = ({ valeur = '', onChange, onScan = null, desactive = false }) => {
  const [tampon, setTampon] = useState('');
  const [actif, setActif] = useState(false);
  const champRef = useRef(null);
  const minuteur = useRef(null);

  useEffect(() => () => clearTimeout(minuteur.current), []);

  const auClavier = (e) => {
    // Touches de modification : sans effet.
    if ([16, 17, 18].includes(e.keyCode)) return;

    if (e.keyCode === 13) {
      e.preventDefault();
      const lu = tampon.trim();
      setTampon('');
      clearTimeout(minuteur.current);
      if (lu) {
        onChange?.(lu);
        onScan?.(lu);
      }
      return;
    }

    // Seuls les caractères alphanumériques composent un identifiant de badge.
    const estChiffre = e.keyCode >= 48 && e.keyCode <= 57;
    const estLettre = e.keyCode >= 65 && e.keyCode <= 90;
    if (!estChiffre && !estLettre) {
      e.preventDefault();
      return;
    }

    e.preventDefault();
    setTampon((precedent) => precedent + String.fromCharCode(e.keyCode));

    clearTimeout(minuteur.current);
    minuteur.current = setTimeout(() => setTampon(''), DELAI_REMISE_A_ZERO_MS);
  };

  const libelle = valeur || (actif ? 'Approchez le badge du lecteur…' : 'Cliquez ici, puis scannez le badge');

  return (
    <div className="space-y-2">
      <div className="relative">
        {/* Champ transparent : il capte les frappes du lecteur sans rien afficher. */}
        <input
          ref={champRef}
          type="text"
          value=""
          onChange={() => {}}
          onKeyDown={auClavier}
          onFocus={() => setActif(true)}
          onBlur={() => { setActif(false); setTampon(''); }}
          disabled={desactive}
          aria-label="Scanner un badge RFID"
          className="absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
        />

        <div
          onClick={() => champRef.current?.focus()}
          className={`flex h-11 items-center justify-between gap-3 rounded-lg border-2 px-4 transition-all ${desactive
            ? 'border-gray-200 bg-gray-50 text-gray-400'
            : actif
              ? 'border-[#246990] bg-[#eef7fb] ring-2 ring-[#246990]/15'
              : valeur
                ? 'border-[#246990] bg-[#eef7fb]'
                : 'border-gray-200 bg-gray-50'
            }`}
        >
          <span className="flex min-w-0 items-center gap-2">
            <CreditCard size={16} className={valeur ? 'text-[#246990]' : 'text-gray-400'} />
            <span className={`truncate text-sm ${valeur ? 'font-semibold text-gray-900' : 'text-gray-500'}`}>
              {libelle}
            </span>
          </span>

          {valeur
            ? <Check size={16} className="flex-shrink-0 text-[#246990]" />
            : actif && <span className="flex-shrink-0 text-[11px] font-semibold text-[#246990]">en attente…</span>}
        </div>
      </div>

      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] leading-snug text-gray-500">
          {valeur
            ? 'Badge lié. Un nouveau scan remplace celui-ci.'
            : 'Le lecteur saisit le badge comme un clavier : le champ doit avoir le focus.'}
        </p>
        {valeur && !desactive && (
          <button
            type="button"
            onClick={() => onChange?.('')}
            className="inline-flex h-8 flex-shrink-0 items-center gap-1.5 rounded-md border border-gray-200 px-3 text-xs font-semibold text-gray-600 transition-colors hover:border-red-300 hover:text-red-600"
          >
            <X size={13} />
            Détacher
          </button>
        )}
      </div>
    </div>
  );
};

export default ScanBadgeRfid;
